import React, { useContext } from "react";
import axios from "axios";
import { Link, useLocation, useHistory } from "react-router-dom";
import FeatherIcon from "feather-icons-react";
import { useTranslation } from "react-i18next";
import AuthContext from "../contexts/AuthContext";
import { menuItems, publicMenuItems, matchHref, getModeCounterpart } from "./headers/MainHeader";
import { prefetchUrl, getDefaultTabUrl } from "../utils/tabPrefetch";
import useAxios from "../utils/useAxios";
// Compact Table temporarily disabled - see ThemeContext.js.
// import { useTheme } from "../contexts/ThemeContext";

// pinnedItems: replaces the mode-switch <li> this normally puts in its own
// pinned (non-scrolling) left slot. The tournament page passes its own
// controls there - its mode switch is a ?admin=1 query param, not one of
// getModeCounterpart's /admin/* route pairs, so this component's own
// version can't express it (modeCounterpart resolves to null there anyway).
// onTournamentPage: purely a CSS marker (see .site-bottom-nav.on-tournament-
// page in index.css) - the tournament page has no .main-header above it, so
// the same "~ .main" rules the old .tournament-bottom-nav carried have to
// keep applying now that this nav replaced it there.
const BottomNav = ({ pinnedItems = null, onTournamentPage = false }) => {
    const { user } = useContext(AuthContext);
    const location = useLocation();
    const history = useHistory();
    const { t } = useTranslation();
    const authedApi = useAxios();
    // Compact Table temporarily disabled - see ThemeContext.js.
    // const { compactTable, setCompactTable } = useTheme();
    const isAdminMode = location.pathname.startsWith("/admin");
    const isAdminItems = user && isAdminMode;
    const items = isAdminItems ? menuItems : publicMenuItems;
    // the public nav items (e.g. "Клуби") land on a page that fetches with
    // a plain, unauthenticated axios call (see ClubsList.js) - prefetching
    // that same URL with the logged-in user's JWT attached instead got the
    // backend to scope ?all=true down to just their own club, and the
    // shared-by-URL prefetch cache (tabPrefetch.js) then handed that
    // wrongly-scoped response straight to the real page
    const prefetchApi = isAdminItems ? authedApi : axios;
    const modeCounterpart = user ? getModeCounterpart(location.pathname) : null;
    // Best-effort href (middle-click/open-in-new-tab); see goToModeCounterpart
    // below for why the real navigation can't just rely on this.
    const modeCounterpartHref = modeCounterpart ? `${modeCounterpart}${location.search}` : modeCounterpart;
    // TournamentsList.js writes its filter chips into the URL via a raw
    // window.history.replaceState call (not react-router's own history), so
    // react-router's location.search above quietly falls behind the real
    // address bar the moment a filter changes - a href built from it goes
    // stale. Reading window.location.search fresh at click time is the only
    // way to actually carry the current filters across the mode switch (see
    // Header.js's matching comment).
    const goToModeCounterpart = (e) => {
        if (!modeCounterpart) return;
        e.preventDefault();
        history.push(`${modeCounterpart}${window.location.search}`);
    };

    return (
        <nav className={`site-bottom-nav${onTournamentPage ? " on-tournament-page" : ""}`}>
            <div className="site-bottom-nav-row">
                <ul className="site-bottom-nav-pinned">
                    {pinnedItems}
                    {!pinnedItems && modeCounterpart && (
                        <li className="mode-switch">
                            <Link to={modeCounterpartHref} onClick={goToModeCounterpart}>
                                <FeatherIcon icon={isAdminMode ? "edit" : "eye"} size={20} />
                                <span>{t(isAdminMode ? "Admin" : "Public View")}</span>
                            </Link>
                        </li>
                    )}
                    {/* User-facing-only density preference - an admin on their own
                        /admin/* list has no use for it, only the Public View of the
                        same list does (see TournamentsList.js/ClubsList.js/etc.'s own
                        compactTable && !is_authorized gate).
                        Compact Table temporarily disabled - see ThemeContext.js.
                    {!isAdminMode && (
                        <li className="compact-switch">
                            <button type="button" onClick={() => setCompactTable(!compactTable)}>
                                <FeatherIcon icon={compactTable ? "maximize-2" : "minimize-2"} size={20} />
                                <span>{compactTable ? t("Compact") : t("Standard mode")}</span>
                            </button>
                        </li>
                    )} */}
                </ul>
                {/* Site-wide nav (Турніри/Клуби/Гравці/Локації) - dropped on the
                    tournament page itself ONLY when pinnedItems actually has
                    something in it (mode-switch/Settings, for an editor of
                    THIS tournament) - that's the only case where the general
                    site sections would be redundant clutter. A guest, or an
                    admin viewing a tournament/club they don't own, gets no
                    pinnedItems at all (Header.js's showTournamentBottomNav
                    gates on can_edit) - without this fallback they'd be left
                    with a completely empty bottom bar instead of just not
                    getting the editor-only controls. */}
                {(!onTournamentPage || !pinnedItems) && (
                    <ul className="site-bottom-nav-scroll">
                        {items.map((item) => (
                            <li key={item.href} className={matchHref(location, item.href) ? "active" : ""}>
                                <Link
                                    to={item.href}
                                    onPointerDown={() => prefetchUrl(prefetchApi, getDefaultTabUrl(item.href))}
                                >
                                    <FeatherIcon icon={item.icon} size={20} />
                                    <span>{t(item.key)}</span>
                                </Link>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </nav>
    );
};

export default BottomNav;
